import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { pick } from 'lodash';
import { shallow } from 'zustand/shallow';

import {
  AboutWEACashPopup,
  WEATeacherReferralRewardsInfoPopup,
} from '../../components/Popups';
import { LearningPackageReferralTemplateProps } from '../../components/Templates';
import { WEATeacherReferralOverviewTemplate } from '../../components/Templates/WEA/WEATeacherReferralOverviewTemplate';
import { AppToastInfoOption } from '../../constants/toast';
import { WebPageType } from '../../constants/webPageType';
import { usePopup, useShareData } from '../../context';
import useFetchList, { FetchListFn } from '../../hooks/useFetchList';
import useRedirectEntry from '../../hooks/useRedirectEntry';
import { ReferralSystem, RewardReferralType } from '../../models/RewardStore';
import {
  useRewardStore,
  useRewardStoreListField,
} from '../../stores/RewardStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';

const { getCurrentUser } = useUserStore.getState();

const WEATeacherReferralOverviewScreen: React.FC = () => {
  const { showPopup, hidePopup } = usePopup();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const activeUserId = useSessionStore((store) => store.activeUserId);

  const [referralCode, setReferralCode] = useState<string>();
  const { setSharedData } = useShareData();

  useEffect(() => {
    setSharedData(undefined);
  }, [setSharedData]);

  useRedirectEntry({ webPageType: WebPageType.WEA });

  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );

  const userAccount = useUserStore((state) =>
    state.getAccount(activeUserId ?? ''),
  );

  const currency = useMemo(() => {
    return userProfile?.localCurrency ?? 'USD';
  }, [userProfile]);

  const rewardReferralField = useRewardStoreListField(
    'referrals',
    RewardReferralType.Referral,
  );

  const fetchRewardReferrals = useCallback<FetchListFn>(
    (cursor) =>
      useRewardStore.getState().fetchRewardReferrals(
        {
          referralSystem: ReferralSystem.Joyajoy, // WeaMembershipPlan
          fieldName: RewardReferralType.Referral,
        },
        cursor,
      ),
    [],
  );

  const {
    fetchMore: fetchMoreRewardReferrals,
    refresh: refreshRewardReferrals,
    hasCursor,
  } = useFetchList(rewardReferralField, fetchRewardReferrals);

  useEffect(() => {
    if (activeUserId) {
      getCurrentUser().catch(console.error);
      refreshRewardReferrals().catch(console.error);
    }
  }, [activeUserId, refreshRewardReferrals]);

  const loadMoreItems = useCallback(() => {
    fetchMoreRewardReferrals().catch(console.error);
  }, [fetchMoreRewardReferrals]);

  const rewardReferrals = useRewardStore((state) => {
    return state
      .getRewardReferrals(RewardReferralType.Referral)
      .map((rewardReferral) =>
        pick(rewardReferral, [
          'userId',
          'referredBy',
          'lastPurchasedAt',
          'tier1Reward',
        ]),
      );
  }, arrayEqual(shallow));

  const referralUserData = useMemo<
    LearningPackageReferralTemplateProps['data']
  >(() => {
    return (
      rewardReferrals.map((rewardReferral) => {
        const referralProfile = useUserStore
          .getState()
          .getProfile(rewardReferral?.userId ?? '');
        // const confirmReward =
        //   rewardReferral.tier1Reward.confirmed?.convertToCurrency(currency);
        // const potentialReward =
        //   rewardReferral.tier1Reward.potential?.convertToCurrency(
        //     currency,
        //     rewardReferral.tier1Reward.confirmed,
        //   );
        if (referralProfile) {
          return {
            profile: referralProfile,
            purchasedAt: rewardReferral.lastPurchasedAt,
            confirmReward: undefined,
            potentialReward: undefined,
            // confirmReward: confirmReward
            //   ? `${currency}${confirmReward}`
            //   : undefined,
            // potentialReward: potentialReward
            //   ? `${currency}${potentialReward}`
            //   : undefined,
            referralCount:
              referralProfile.statistics.joyajoyCustomerReferralCount,
          };
        } else {
          return null;
        }
      }) ?? []
    ).filter(Boolean) as LearningPackageReferralTemplateProps['data'];
  }, [rewardReferrals]);

  const onClickInfo = useCallback(() => {
    showPopup(<WEATeacherReferralRewardsInfoPopup onPrimary={hidePopup} />);
  }, [showPopup, hidePopup]);

  const getReferralCode = useCallback(async () => {
    const referralCodeData = await useRewardStore
      .getState()
      .getRewardReferralCode(ReferralSystem.Joyajoy); // WeaMembershipPlan
    if (referralCodeData) {
      setReferralCode(referralCodeData.referralCode);
    }
  }, []);

  useEffect(() => {
    getReferralCode().catch(console.error);
  }, [getReferralCode]);

  const onClickShare = useCallback(() => {
    const pageUrl = window.location.href;

    const updatedUrl = new URL(pageUrl);
    updatedUrl.pathname = '/wea-member';

    const queryParams = new URLSearchParams();
    queryParams.set('referralCode', referralCode ?? '');
    updatedUrl.search = queryParams.toString();

    if (navigator.share) {
      navigator.share({ url: updatedUrl.toString() }).catch((err) => {
        console.error('Failed to share the URL: ', err);
      });
    } else {
      navigator.clipboard
        .writeText(updatedUrl.toString())
        .then(() => {
          // setCopied(true);
          // setTimeout(() => setCopied(false), 2000); // reset after 2s
          toast(t('Link is copied!'), {
            ...AppToastInfoOption,
            id: 'clipboard',
          });
        })
        .catch((err) => {
          console.error('Failed to copy the URL: ', err);
        });
    }
  }, [referralCode, t]);

  const onClickBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onClickCashInfo = useCallback(() => {
    showPopup(<AboutWEACashPopup onPrimary={hidePopup} />);
  }, [showPopup, hidePopup]);

  return (
    <WEATeacherReferralOverviewTemplate
      totalReferralUserCount={
        userProfile?.statistics.joyajoyCustomerReferralCount ?? 0
      }
      profile={undefined}
      availableReward={`${currency}${
        userAccount?.weaCashBalance.available.convertToCurrency(currency) ?? '0'
      }`}
      confirmReward={`${currency}${
        userAccount?.weaCashBalance.confirmed.convertToCurrency(
          currency,
          userAccount.weaCashBalance.available,
        ) ?? '0'
      }`}
      potentialReward={`${currency}${
        userAccount?.weaCashBalance.potential.convertToCurrency(
          currency,
          userAccount.weaCashBalance.confirmed,
          userAccount.weaCashBalance.available,
        ) ?? '0'
      }`}
      data={referralUserData}
      hasCursor={hasCursor}
      onClickInfo={onClickInfo}
      onClickCashInfo={onClickCashInfo}
      onClickShare={onClickShare}
      onClickBack={onClickBack}
      loadMoreItems={loadMoreItems}
    />
  );
};
export default WEATeacherReferralOverviewScreen;
